/**
 * Shared, backend-aligned status semantics for the global job-progress UX.
 *
 * All admin async jobs (shop-reset, mockup-regen, listing-replace) + bulk-upload
 * jobs share a small status vocabulary. We normalize it to four UI states so one
 * modal/toast can render any job. Aligned with the backend mixin's terminal set —
 * NOT the older per-panel frontend sets (some of which omitted `cancelled`).
 */

export const PAUSED_STATUSES = new Set(['paused_needs_input']);
// `deferred` (Etsy rate-limit) + `duplicate` are non-error terminal outcomes.
export const TERMINAL_OK_STATUSES = new Set(['succeeded', 'partial', 'duplicate', 'deferred']);
export const TERMINAL_ERR_STATUSES = new Set(['failed', 'cancelled']);

/** Map a raw `status` to one of: active | paused | terminal-ok | terminal-err. */
export function mapStatus(status) {
  if (PAUSED_STATUSES.has(status)) return 'paused';
  if (TERMINAL_ERR_STATUSES.has(status)) return 'terminal-err';
  if (TERMINAL_OK_STATUSES.has(status)) return 'terminal-ok';
  return 'active'; // queued / running / unknown-in-progress
}

export const isTerminal = status => TERMINAL_OK_STATUSES.has(status) || TERMINAL_ERR_STATUSES.has(status);

export const isPaused = status => PAUSED_STATUSES.has(status);

/** `partial` succeeded-with-skips → a warning tint rather than a clean success. */
export const isWarning = status => status === 'partial';

/** Humanized `phase` → step headline, per job kind. */
export const PHASE_LABELS = {
  'shop-reset': {
    queued: 'Queued',
    deleting_designs: 'Deleting designs',
    reingesting: 'Re-ingesting designs',
    deleting_mockups: 'Deleting old mockups',
    regenerating_mockups: 'Regenerating mockups',
    replacing_listings: 'Replacing listings',
    done: 'Done',
  },
  'mockup-regen': {
    queued: 'Queued',
    regenerating: 'Regenerating mockups',
    done: 'Done',
  },
  'listing-replace': {
    queued: 'Queued',
    pulling: 'Pulling listings from Etsy',
    replacing: 'Replacing listings',
    done: 'Done',
  },
};

export function phaseLabel(kind, phase) {
  const map = PHASE_LABELS[kind];
  return (map && map[phase]) || phase || '';
}
